import { isBirthday } from '../lib/birthday.js'

/**
 * One scheduled entry. The visual language carries three facts a scheduler needs at a
 * glance: what the work is, who booked it if that is not the obvious answer, and whether
 * this person has been borrowed from somewhere else.
 */
export default function Entry({
  entry,
  board,
  jobTypes,
  groupsById,
  showBorrow,
  crewed = false,
  crewStyle,
  crewName,
}) {
  const jobType = jobTypes?.get(entry.jobTypeId)
  // Falls back to the tenant's own setting rather than to `true`. It defaulted to true and
  // no call site ever passed it, so the "mark borrowed staff" switch in Group setup was
  // wired to nothing at all: the markers were on for everybody, whatever the switch said.
  const show = showBorrow ?? board?.settings?.showHomeTeamMarker ?? false
  // The unassigned pseudo-group is not a crew, so nobody can be borrowed FROM it. It has a
  // null id, which the guard below already rejects — this keeps that true if the group ever
  // gains a real id, since "Not on a team" is a place people are absent from a crew, not a
  // crew they belong to.
  const from = entry.borrowedFromGroupId ? groupsById?.get(entry.borrowedFromGroupId) : null
  const borrowedFrom = from && from.kind !== 'unassigned' ? from : null

  // Hovering an entry surfaces the scheduler's own note — "call the customer before you
  // go" is the thing worth reading, and it is otherwise two clicks inside the editor. The
  // group name rides above it on a people board, where it is the one fact the row heading
  // no longer carries.
  const tip = [crewed ? crewName : null, entry.internalNote].filter(Boolean).join('\n') || undefined

  return (
    <div
      className={`entry ${borrowedFrom && show ? 'borrowed' : ''} ${crewed ? 'crewed' : ''} ${
        isBirthday(entry.label) ? 'birthday-entry' : ''
      }`}
      style={crewStyle}
      title={tip}
    >
      {/* The cake and the confetti are drawn by CSS on this class alone — decoration belongs
          in the stylesheet, and a screen reader is already being told "MY BIRTHDAY" by the
          label, which is how the cell was recognised in the first place. */}
      <span className="label">{entry.label}</span>
      <span className="meta">
        {/* A people board says the job and nothing else. The row is already the person and
            the colour is already the group, so the skill chip and a chip repeating that
            colour's name were three ways of saying what one line and one stripe say. */}
        {!crewed && jobType && <span className="chip">{jobType.name}</span>}
        {borrowedFrom && show && (
          <span className="chip" title={`Borrowed from ${borrowedFrom.name}`}>
            {borrowedFrom.color && (
              <span className="group-swatch" style={{ background: borrowedFrom.color }} />
            )}
            ← {borrowedFrom.name}
          </span>
        )}
        {/* In normal mode the booking PM's initials only appear when it is NOT the person
            whose block this row sits in — otherwise every cell would carry noise. */}
        {board.mode === 'normal' && borrowedFrom && show && entry.ownerPmInitials && (
          <span className="chip">{entry.ownerPmInitials}</span>
        )}
      </span>
    </div>
  )
}

